import { useState } from "react";
import { Copy } from "lucide-react";
import { t } from "@/lib/i18n";
import { copyText } from "@/lib/utils";
import { Card } from "@/components/ui/Card";

/**
 * Anbindungs-Kochbuch: Copy-paste-fertige Beispiele für alle Ingest-Wege.
 * HTTP-Beispiele nutzen die echte Server-Adresse (window.location), TCP und
 * Syslog die docker-compose-Default-Ports.
 */
export function IntegrationsPage() {
  const origin = window.location.origin;
  const host = window.location.hostname;
  const uiPort = window.location.port || (window.location.protocol === "https:" ? "443" : "80");

  const tcpExample = `Host: ${host}   Port: 49090

# 1. Auth-Frame (eine JSON-Zeile, API-Key aus Quellen → TCP-Clients).
#    Optional mit Last Will: Leif veröffentlicht es, wenn die Verbindung
#    abreißt, ohne dass vorher {"disconnect":true} kam.
{"client_id":"zelle-a3","api_key":"<api-key>","last_will":{"message":"Zelle A3 unerwartet getrennt","service":"greifer"}}

# 2. Danach ein Event pro Zeile:
{"level":"INFO","message":"Zyklus fertig","service":"greifer","host":"dobot-a3"}
{"level":"ERROR","message":"Vakuum nicht erreicht","trace_id":"auftrag-4711"}

# 3. Sauber abmelden (entschärft das Last Will):
{"disconnect":true}`;

  const mqttExample = `# Gerät publiziert direkt an Leif, ohne eigenen Broker.
# Login: Benutzername = Client-ID, Passwort = API-Key.
mosquitto_pub -h ${host} -p 41883 -u zelle-a3 -P <api-key> \\
  -t werk/zelle-a3/status \\
  -m '{"level":"ERROR","message":"Vakuum nicht erreicht"}'

# Offline-Erkennung: Last Will beim Verbinden hinterlegen —
# reißt die Verbindung ab, veröffentlicht Leif das Event selbst.
mosquitto_pub -h ${host} -p 41883 -u zelle-a3 -P <api-key> \\
  --will-topic werk/zelle-a3/offline \\
  --will-payload '{"message":"Zelle A3 unerwartet getrennt"}' \\
  -t werk/zelle-a3/status -m '{"message":"online"}'`;

  const restExample = `curl -X POST ${origin}/api/logs \\
  -H "Authorization: Bearer <api-key>" \\
  -H "Content-Type: application/json" \\
  -d '{"level":"WARN","message":"Temperatur hoch","service":"ofen-2","metadata":{"temp":92.1}}'`;

  const promtailExample = `# promtail-config.yml
clients:
  - url: ${origin}/loki/api/v1/push
    bearer_token: <api-key>

scrape_configs:
  - job_name: maschinen-logs
    static_configs:
      - targets: [localhost]
        labels:
          service: gateway
          __path__: /var/log/maschine/*.log`;

  const vectorExample = `# vector.toml
[sinks.leif]
type = "loki"
inputs = ["mein_input"]
endpoint = "${origin}"
auth.strategy = "bearer"
auth.token = "<api-key>"
labels.service = "vector"
encoding.codec = "text"`;

  const fluentBitExample = `# fluent-bit.conf
[OUTPUT]
    name          loki
    match         *
    host          ${host}
    port          ${uiPort}
    bearer_token  <api-key>
    labels        service=fluent-bit`;

  const rsyslogExample = `# /etc/rsyslog.d/50-leif.conf — eine der beiden Zeilen reicht:
*.* @${host}:49514     # UDP
#*.* @@${host}:49514   # TCP (zuverlässiger)`;

  const otlpCollectorExample = `# otel-collector.yaml
exporters:
  otlphttp/leif:
    endpoint: ${origin}
    headers:
      X-Client-Id: zelle-a3
      X-Api-Key: <api-key>

service:
  pipelines:
    logs:
      receivers: [otlp]
      exporters: [otlphttp/leif]`;

  const otlpSdkExample = `# Direkt aus dem SDK (ohne Collector), z.B. als Env-Vars:
OTEL_EXPORTER_OTLP_LOGS_ENDPOINT=${origin}/v1/logs
OTEL_EXPORTER_OTLP_HEADERS=X-Client-Id=zelle-a3,X-Api-Key=<api-key>`;

  const qrmaintUrl = `${origin}/api/webhook/qrmaint`;

  return (
    <div className="flex flex-col gap-4 p-6">
      <p className="max-w-3xl text-sm text-muted-foreground">
        {t("Copy-paste-fertige Beispiele für alle Ingest-Wege. API-Keys legst du unter Quellen → TCP-Clients an — derselbe Key gilt für TCP, REST und die Loki-API.")}
      </p>

      <Card
        title={t("TCP: Roboter & eigene Clients")}
        description={t("Zeilenweises JSON über eine TCP-Verbindung. Erste Zeile authentifiziert, danach ein Event pro Zeile.")}
      >
        <CodeBlock code={tcpExample} />
      </Card>

      <Card
        title={t("REST: Skripte & Webhooks")}
        description={t("Für Skripte, Cron-Jobs und alles, was HTTP kann: ein POST pro Event.")}
      >
        <CodeBlock code={restExample} />
      </Card>

      <Card
        title="OTLP: OpenTelemetry"
        description={t("Standard-Logs-Endpoint unter /v1/logs (Protobuf und JSON, gzip). service.name und host.name aus den Resource-Attributen landen in den Leif-Feldern, Trace-IDs bleiben erhalten.")}
      >
        <div className="flex flex-col gap-3">
          <CodeBlock code={otlpCollectorExample} />
          <CodeBlock code={otlpSdkExample} />
        </div>
      </Card>

      <Card
        title={t("Loki-API: Promtail")}
        description={t("Promtail liest lokale Log-Dateien und pusht sie an Leifs Loki-API — ideal für bestehende Textlogs auf Gateways.")}
      >
        <CodeBlock code={promtailExample} />
      </Card>

      <Card
        title={t("Loki-API: Vector")}
        description={t("Vector als Sammler: beliebige Inputs (Datei, Journald, Socket), Loki-Sink auf Leif.")}
      >
        <CodeBlock code={vectorExample} />
      </Card>

      <Card
        title={t("Loki-API: Fluent Bit")}
        description={t("Fluent Bit auf Edge-Geräten: leichtgewichtig, Loki-Output auf Leif.")}
      >
        <CodeBlock code={fluentBitExample} />
      </Card>

      <Card
        title={t("MQTT: Direktempfang")}
        description={t("Sensoren, SPSen und Gateways publizieren direkt an Leif, ohne eigenen Broker (MQTT 3.1.1, QoS 0/1). Mit Last Will meldet Leif Geräte-Abrisse von selbst. Vorher unter Quellen → MQTT aktivieren.")}
      >
        <CodeBlock code={mqttExample} />
      </Card>

      <Card
        title={t("Syslog: rsyslog & Netzwerkgeräte")}
        description={t("Systemlogs per rsyslog weiterleiten; Switches, Firewalls und USVs zeigen auf denselben Port. Vorher unter Quellen → Syslog aktivieren.")}
      >
        <CodeBlock code={rsyslogExample} />
      </Card>

      <Card
        title="QR.Maint Webhook"
        description={t("QR.Maint sendet Instandhaltungs-Events per Webhook an Leif. Secret und Status-Mapping konfigurierst du unter Quellen → QR.Maint Webhook.")}
      >
        <CodeBlock code={qrmaintUrl} />
      </Card>

      <p className="text-[11px] text-muted-foreground">
        {t("Ports entsprechen den docker-compose-Defaults (49090 TCP-Ingest, 49514 Syslog). Bei angepassten Port-Mappings entsprechend ändern.")}
      </p>
    </div>
  );
}

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    void copyText(code).then((ok) => {
      if (ok) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    });
  };

  return (
    <div className="relative rounded-md bg-muted/60">
      <button
        type="button"
        onClick={copy}
        className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-[11px] hover:bg-muted"
      >
        <Copy className="h-3 w-3" /> {copied ? t("Kopiert!") : t("Kopieren")}
      </button>
      <pre className="overflow-x-auto px-3 py-2 pr-24 font-mono text-xs leading-relaxed">{code}</pre>
    </div>
  );
}
